import React, { useEffect, useState } from 'react';
import ReactSelect, { ActionMeta, InputActionMeta, SelectInstance as ReactSelectInstance } from 'react-select';
import Tooltip from '../tooltip/Tooltip';
import components from './SelectComponents';
import { SelectContext } from './SelectContext';
import { SelectOption } from './types';

const DEBOUNCE_RATE = 250;

export type SelectInstance = ReactSelectInstance;

export type AsyncSelectProps = {
    /**
     * Size of select component.
     */
    size?: 'standard' | 'small';

    /**
     * The value of the placeholder attribute on the select.
     */
    placeholder?: string;

    /**
     * Options the user can select.
     */
    options?: SelectOption[];

    /**
     * Shows the select in a loading state.
     */
    isLoading?: boolean;

    /**
     * Disables to select input.
     */
    isDisabled?: boolean;

    /**
     * Tooltip to explain why the select is disabled.
     */
    disabledHint?: string;

    /**
     * Allows the user to select multiple options.
     */
    isMulti?: boolean;

    /**
     * The title explaining the use of the input.
     */
    title?: string;

    /**
     * Associate this input with a form.
     */
    form?: string;

    /**
     * Displays an "optional" hint.
     */
    isOptional?: boolean;

    /**
     * Styles the dropdown menu a 'relative' instead of 'absolute'.
     */
    showMenuAsRelative?: boolean;

    /**
     * Allows the user to clear the selection
     */
    isClearable?: boolean;

    /**
     * Display an error message under the input in red.
     */
    errorMessage?: string;

    /**
     * Displays a hint under the component.
     */
    hint?: string;

    /**
     * Border style
     */
    borderStyle?: 'border' | 'underline';

    /**
     * Callback function invoked when the user searches.
     */
    onSearch?: (input: string) => void;

    /**
     * Callback function invoked when an option(s) is select/deselected.
     */
    onChange?: (options: SelectOption[]) => void;
};

const AsyncSelect = React.forwardRef(function AsyncSelect(
    {
        placeholder = 'Select...',
        size = 'standard',
        isLoading = false,
        isMulti = false,
        isDisabled = false,
        showMenuAsRelative = false,
        isClearable = true,
        disabledHint,
        errorMessage,
        hint,
        form,
        options,
        isOptional,
        borderStyle = 'border',
        title,
        onSearch,
        onChange,
    }: AsyncSelectProps,
    ref: React.Ref<SelectInstance>
): JSX.Element {
    const [search, setSearch] = useState<string | undefined>();

    function handleInputChange(input: string, { action }: InputActionMeta): void {
        if (action === 'input-change') {
            setSearch(input);
        }
    }

    function handleChange(options: SelectOption | SelectOption[], { action }: ActionMeta<unknown>): void {
        if (onChange) {
            if (Array.isArray(options)) {
                onChange(options);
            } else {
                // No options is actually null not undefined
                onChange(options ? [options] : []);
            }
        }
        if (action === 'clear') {
            setSearch('');
        }
    }

    function handleBlur(): void {
        if (onSearch) {
            onSearch('');
            // setSearch('');
        }
    }

    /**
     * Whenever the value changes don't fire the onChange event until the timeout has completed.
     * Everytime a new value is entered; reset the timeout
     */
    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (search !== undefined && onSearch) {
            timeout = setTimeout(() => onSearch(search), DEBOUNCE_RATE);
        }
        return (): void => timeout && clearTimeout(timeout);
    }, [search, onSearch]);

    return (
        <div>
            {/* Title & Optional */}
            <div className="flex justify-between">
                {/* Show a title */}
                {title && <label className="block text-sm font-medium text-gray-700 mb-1">{title}</label>}
                {/* Show an optional message */}
                {isOptional && <span className="text-sm text-gray-500 ml-auto mb-1">Optional</span>}
            </div>
            <Tooltip message={disabledHint} disabled={!disabledHint}>
                <SelectContext.Provider value={{ size, position: showMenuAsRelative ? 'relative' : 'absolute', borderStyle, errorMessage }}>
                    <ReactSelect
                        form={form}
                        placeholder={placeholder}
                        components={components}
                        isMulti={isMulti}
                        isLoading={isLoading}
                        isDisabled={isDisabled}
                        isClearable={isClearable}
                        options={isLoading ? undefined : options}
                        isOptionDisabled={(o: SelectOption): boolean => o.isDisabled}
                        onInputChange={handleInputChange}
                        value={options.filter((o) => o.isSelected)}
                        filterOption={null}
                        onChange={handleChange}
                        closeMenuOnSelect={!isMulti}
                        onBlur={handleBlur}
                        ref={ref}
                    />
                </SelectContext.Provider>
            </Tooltip>
            <div className="flex">
                {/* Hint */}
                {hint && !errorMessage && <p className="mt-2 text-sm text-gray-500">{hint}</p>}
                {/* Error Message */}
                {errorMessage && <p className="mt-2 text-sm text-red-600">{errorMessage}</p>}
            </div>
        </div>
    );
});

export default AsyncSelect;
